import { type FC, useEffect, useRef, useState } from "react";

import styled from "styled-components";

import { colorsPalettes, setFontVariant } from "@similarweb/styles";
import { VirtualListWithFade } from "@similarweb/ui-components";

import { allTrackers } from "app/services/track/track";

import { useKeywordListComposerContext } from "../KeywordListComposerContext";
import type { Keyword } from "../types";

import { Headers } from "./Headers";
import { KeywordItem } from "./KeywordItem";

const { carbon, blue } = colorsPalettes;

interface KeywordsListProps {
    displayHeaders?: boolean;
    displayActionItems?: boolean;
}

const BACKGROUND_COLOR = blue[50];

const KEYWORD_ITEM_HEIGHT = 35;
const HEADERS_HEIGHT = 40;

const DisplayedOnHover = styled.div<{ isHidden?: boolean }>`
    opacity: 0;
`;

const DisplayNone = styled.div`
    display: none;
`;

const KeywordItemContainer = styled.div<{ $isHovered: boolean; $pointerEvents?: "none" | "all" }>`
    cursor: pointer;
    height: ${KEYWORD_ITEM_HEIGHT}px;
    align-items: center;
    display: flex;
    justify-content: space-between;
    padding: 0px 16px;
    pointer-events: ${({ $pointerEvents = "all" }) => $pointerEvents};
    ${setFontVariant("input2", carbon["400"])}
    ${(props) =>
        props.$isHovered &&
        `
            background-color: ${BACKGROUND_COLOR};
            ${DisplayedOnHover} {
                opacity: 1;
            }
    `}
    transition: all 300ms ease-in-out;
`;

const KeywordItemContainerWrapper = styled(KeywordItemContainer)`
    pointer-events: none;
`;

const Container = styled.div`
    flex: 1 1 auto;
    padding: 8px 0px;
    overflow-y: hidden;
`;

const HoverContainer = styled.div`
    &:hover {
        ${KeywordItemContainer} {
            background-color: transparent;
            ${DisplayedOnHover} {
                opacity: 0;
            }
            &:hover {
                background-color: ${BACKGROUND_COLOR};
                ${DisplayedOnHover} {
                    transition: all 300ms ease-in-out;
                    opacity: 1;
                }
            }
        }
    }
`;

const getTopAndBottomPadding = (element: HTMLElement) => {
    if (!element) {
        return 0;
    }
    const style = getComputedStyle(element);
    const { paddingTop, paddingBottom } = style;
    const padding = parseFloat(paddingTop) + parseFloat(paddingBottom);
    return padding;
};

export const KeywordsList: FC<KeywordsListProps> = (props) => {
    const { query, keywords, seedKeyword, removeKeywords, selectKeywords } = useKeywordListComposerContext();
    const { displayHeaders = true, displayActionItems = true } = props;
    const [scrollToIndex, setScrollToIndex] = useState<number>();

    const DisplayOnHoverComponent = displayActionItems ? DisplayedOnHover : DisplayNone;
    const KeywordItemContainerComponent = displayActionItems ? KeywordItemContainer : KeywordItemContainerWrapper;
    const keywordsWithoutSeedKeyword = keywords.filter(({ term }) => term !== seedKeyword);
    const containerRef = useRef<HTMLDivElement>(null);
    const [totalHeight, setTotalHeight] = useState(KEYWORD_ITEM_HEIGHT);
    const baseColor = displayActionItems ? carbon[0] : carbon[25];

    useEffect(() => {
        if (containerRef.current) {
            const calculatedHeight =
                containerRef.current.clientHeight -
                (seedKeyword ? KEYWORD_ITEM_HEIGHT : getTopAndBottomPadding(containerRef.current));
            setTotalHeight(calculatedHeight);
        }
    }, [seedKeyword]);

    const onTermClickHof = (keyword: Keyword) => () => {
        const index = keywords.findIndex(({ term }) => term === keyword.term);
        setScrollToIndex(index);
        selectKeywords([keyword], !keyword.selected);
        allTrackers.dispatchTrackEvent("Pop Up", "click", `${keyword.selected ? "uncheck" : "check"}/single`);
    };
    const nonEmptyKeywordList = keywords.length > 0;

    // eslint-disable-next-line @typescript-eslint/no-wrapper-object-types
    const rowRenderer = ({ index, style }: { index: number; style: Object }) => {
        const keyword = keywordsWithoutSeedKeyword[index];
        const hoverIndex = seedKeyword ? 0 : 1;
        const isHovered = index === hoverIndex && displayActionItems;
        const onRemoveKeyword = () => removeKeywords([keyword]);
        const onTermClick = onTermClickHof(keyword);
        return (
            <div
                key={keyword.term}
                style={style}
            >
                <KeywordItem
                    keyword={keyword}
                    isHovered={isHovered}
                    onRemoveKeyword={onRemoveKeyword}
                    Container={KeywordItemContainerComponent}
                    DisplayedOnHover={DisplayOnHoverComponent}
                    query={query}
                    onTermClick={onTermClick}
                />
            </div>
        );
    };
    const renderHeaders = nonEmptyKeywordList && displayHeaders;
    const seedKeywordObject = keywords.find((keyword) => keyword.term === seedKeyword);

    const height = renderHeaders ? totalHeight - HEADERS_HEIGHT : totalHeight;

    return (
        <Container ref={containerRef}>
            {renderHeaders ? <Headers /> : null}
            <HoverContainer>
                {seedKeywordObject ? (
                    <KeywordItem
                        keyword={seedKeywordObject}
                        onRemoveKeyword={() => removeKeywords([seedKeywordObject])}
                        Container={KeywordItemContainerComponent}
                        DisplayedOnHover={DisplayOnHoverComponent}
                        isSeedKeyword={true}
                        onTermClick={onTermClickHof(seedKeywordObject)}
                    />
                ) : null}
                <div style={{ height }}>
                    <VirtualListWithFade
                        containerStyle={{ maxWidth: "100%" }}
                        style={{ width: "100%" }}
                        height={height}
                        width={1}
                        rowCount={keywordsWithoutSeedKeyword.length}
                        rowHeight={KEYWORD_ITEM_HEIGHT}
                        rowRenderer={rowRenderer}
                        scrollToIndex={scrollToIndex}
                        baseColor={baseColor}
                    />
                </div>
            </HoverContainer>
        </Container>
    );
};
